import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import { BeakerIcon, Loader2Icon } from "lucide-react";
import { TestSetService } from "../../../../services/TestSetService";
import { usePermissions } from "../../../../PermissionsProvider";
import TestRunEvaluations from "../../../TestRun/Steps/TestRunEvaluations";

interface TestSetOption {
  id: number;
  name: string;
  description?: string;
  is_golden: boolean;
  intent_group_id?: number;
  request_count?: number;
}

interface AutoTestStepProps {
  selectedTestSetIds: number[];
  setSelectedTestSetIds: (ids: number[]) => void;
  selectedEvalCriteriaIds: number[];
  setSelectedEvalCriteriaIds: (ids: number[]) => void;
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
}

function mapToGolden(items: any[]): TestSetOption[] {
  return items
    .filter((ts: any) => ts.is_golden)
    .map((ts: any) => ({
      id: ts.id,
      name: ts.test_set_name || ts.name,
      description: ts.test_set_description || ts.description,
      is_golden: ts.is_golden,
      intent_group_id: ts.intent_group_id,
      request_count: ts.meta?.request_count ?? ts.request_count ?? undefined,
    }));
}

function extractItems(resp: any): any[] {
  const items = resp?.items ?? resp;
  return Array.isArray(items) ? items : [];
}

const AutoTestStep: React.FC<AutoTestStepProps> = ({
  selectedTestSetIds,
  setSelectedTestSetIds,
  selectedEvalCriteriaIds,
  setSelectedEvalCriteriaIds,
  intentGroupId,
  applicationId,
  intentId,
}) => {
  const { apiKey } = usePermissions();
  const [testSets, setTestSets] = useState<TestSetOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const tryFetchScoped = () => {
      if (intentGroupId) {
        return new Promise<TestSetOption[]>((resolve) => {
          TestSetService.getTestSetsByIntentGroup(
            intentGroupId.toString(),
            0,
            200,
            () => {},
            apiKey
          ).subscribe(
            (resp: any) => resolve(mapToGolden(extractItems(resp))),
            () => resolve([])
          );
        });
      }
      if (applicationId && intentId) {
        return new Promise<TestSetOption[]>((resolve) => {
          TestSetService.getTestSetsByApplicationIntent(
            applicationId,
            intentId,
            0,
            200,
            () => {},
            apiKey
          ).subscribe(
            (resp: any) => resolve(mapToGolden(extractItems(resp))),
            () => resolve([])
          );
        });
      }
      return Promise.resolve([]);
    };

    const fetchCompanyWide = () =>
      new Promise<TestSetOption[]>((resolve) => {
        TestSetService.getTestSets(() => {}, apiKey).subscribe(
          (data: any) =>
            resolve(Array.isArray(data) ? mapToGolden(data) : []),
          () => resolve([])
        );
      });

    (async () => {
      let results = await tryFetchScoped();
      if (results.length === 0 && !cancelled) {
        results = await fetchCompanyWide();
      }
      if (!cancelled) {
        setTestSets(results);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiKey, intentGroupId, applicationId, intentId]);

  const toggleTestSet = (id: number) => {
    if (selectedTestSetIds.includes(id)) {
      setSelectedTestSetIds(selectedTestSetIds.filter((x) => x !== id));
    } else {
      setSelectedTestSetIds([...selectedTestSetIds, id]);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Benchmark Tests
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Select golden test sets to run automatically after fine-tuning
          completes.
        </p>
      </div>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BeakerIcon className="w-5 h-5 text-maitai-green" />
            Golden Test Sets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : testSets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No golden test sets found. You can skip this step or mark a test
              set as golden first.
            </p>
          ) : (
            <div className="space-y-3">
              {testSets.map((ts) => (
                <label
                  key={ts.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                  <Checkbox
                    checked={selectedTestSetIds.includes(ts.id)}
                    onCheckedChange={() => toggleTestSet(ts.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-gc-med text-foreground truncate">
                        {ts.name}
                      </span>
                      {ts.request_count !== undefined && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          {ts.request_count} requests
                        </span>
                      )}
                    </div>
                    {ts.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {ts.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {selectedTestSetIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
              {selectedTestSetIds.length} test set
              {selectedTestSetIds.length !== 1 ? "s" : ""} selected. These will
              run in parallel once fine-tuning completes.
            </p>
          )}
        </CardContent>
      </Card>

      {selectedTestSetIds.length > 0 && (
        <TestRunEvaluations
          selectedCriteriaIds={selectedEvalCriteriaIds}
          onCriteriaChange={setSelectedEvalCriteriaIds}
        />
      )}

      <p className="text-xs text-muted-foreground text-center">
        This step is optional. If no test sets are selected, the finetune run
        will complete without running tests.
      </p>
    </div>
  );
};

export default AutoTestStep;
